import { LoadingDots } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import {
  CodeEditor,
  type CodeEditorProps,
  type MonacoEditor,
  type MonacoEditorDecorations,
} from '../../../CodeEditor';

export interface TransactionContentProps
  extends Pick<CodeEditorProps, 'value' | 'language'> {
  onChange?: (value: string) => void;
  searchValue?: string;
}

export const TransactionContent = ({
  value,
  language,
  onChange,
  searchValue,
}: TransactionContentProps) => {
  const [isFormatting, setIsFormatting] = useBoolean(true);
  const decorationsRef = useRef<MonacoEditorDecorations>(null);
  const editorRef = useRef<MonacoEditor>();

  const formatValue = async () => {
    if (language === 'json') {
      editorRef.current?.updateOptions({
        readOnly: false,
      });

      await editorRef.current?.getAction('editor.action.formatDocument')?.run();

      editorRef.current?.updateOptions({
        readOnly: true,
      });

      editorRef.current?.revealLine(1);
    }

    onChange?.(editorRef.current?.getValue());
    setIsFormatting.setFalse();
  };

  const highlightSearchValue = () => {
    decorationsRef.current?.clear();
    if (searchValue) {
      const matches = editorRef.current
        ?.getModel()
        ?.findMatches(searchValue, false, false, false, null, false);

      decorationsRef.current = editorRef.current?.createDecorationsCollection(
        matches?.map((match) => ({
          range: match.range,
          options: {
            isWholeLine: false,
            inlineClassName: 'ds-code-editor-highlight',
          },
        }))
      );
      const firstMatch = matches?.[0]?.range;
      if (firstMatch) {
        editorRef.current?.revealLineNearTop(firstMatch.startLineNumber);
      }
    }
  };

  useEffect(() => {
    highlightSearchValue();
  }, [searchValue, isFormatting]);

  return (
    <div className="relative">
      {isFormatting && (
        <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center">
          <LoadingDots />
        </div>
      )}
      <div className={classNames('flex-1 w-full', isFormatting && 'opacity-0')}>
        <CodeEditor
          onMount={(editor) => {
            editorRef.current = editor;
            editorRef.current?.onDidChangeModelContent(formatValue);
            setTimeout(() => {
              formatValue();
            }, 200);
          }}
          value={value}
          options={{ readOnly: true }}
          language={language}
        />
      </div>
    </div>
  );
};
